import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import React from 'react';

import { logger } from '@/lib/logger';

type SaveState = 'idle' | 'pending' | 'saving' | 'error';

/**
 * Live content returned by components for stale data validation.
 * Contains action ID and current payload state from the editor.
 */
export interface LiveActionContent {
  actionId: number;
  payload: Record<string, unknown>;
  /** Stable list-position used to match live content to the current server
   * action across version bumps (id changes on a version bump; this doesn't). */
  executionOrder?: number;
}

interface ComponentRegistration {
  saveState: SaveState;
  flushFn: (() => Promise<void>) | null;
  getContentFn: (() => LiveActionContent | null) | null;
  resyncFn: (() => void) | null;
}

interface SubscriberState {
  hasPendingOrSaving: boolean;
  hasError: boolean;
  /** Monotonically increasing counter that increments on every save state change.
   * Used by consumers (e.g., StickyFooter) to re-render when editor content changes,
   * even when hasPendingOrSaving doesn't toggle. */
  contentVersion: number;
}

interface RegistrationHandle {
  setSaveState: (state: SaveState) => void;
  setFlushFn: (fn: () => Promise<void>) => void;
  setContentFn: (fn: () => LiveActionContent | null) => void;
  setResyncFn: (fn: () => void) => void;
  unregister: () => void;
}

interface FlushResult {
  success: boolean;
  errors: Error[];
}

export interface AutoSaveCoordinationContextValue {
  /**
   * Register a component for save state coordination.
   * Returns handle with setSaveState, setFlushFn, setContentFn, and unregister functions.
   */
  register: (id: string) => RegistrationHandle;
  /**
   * Subscribe to aggregated state changes.
   * Returns unsubscribe function.
   */
  subscribe: (callback: (state: SubscriberState) => void) => () => void;
  /**
   * Flush all registered components and wait for completion.
   * Returns success status and any errors encountered.
   *
   * Pass `excludeIds` to skip specific registrations — e.g. a component that
   * owns its own final-commit flush (and would otherwise double-save if its
   * registered flushFn also ran here).
   */
  flushAllAndWait: (options?: {
    excludeIds?: string[];
  }) => Promise<FlushResult>;
  /**
   * Collect current content from all registered components.
   * Used by StickyFooter to get live editor content for stale data validation.
   */
  collectAllContent: () => LiveActionContent[];
  /**
   * Freeze autosave across all registered components. Called by the approval
   * footers right before the approve/reject POST — and by a card's own "I don't
   * have this information" exit right before its reassign/reject POST: once the
   * proposal leaves the `pending` state, any update_payload call is rejected by
   * the backend (`UPDATE_FAILED`), so components must neither arm new debounce
   * timers nor let already-armed timers fire. Components check
   * `isAutoSaveFrozen()` at both points. The flush-before-approval path is
   * unaffected — it runs before the freeze.
   *
   * Returns a unique owner token identifying THIS freeze. The freeze is
   * reference-counted across all live owners: multiple independent decisions
   * (a footer approve/reject AND a card exit) can hold it at once, and it stays
   * frozen until EVERY owner has released. Pass the token back to
   * `unfreezeAutoSave(token)` so each caller releases only its own hold —
   * releasing one owner never clears a freeze another owner still needs held,
   * regardless of the order in which they resolve.
   */
  freezeAutoSave: () => number;
  /**
   * Release this caller's hold on the autosave freeze. Called when an
   * approve/reject/exit attempt fails terminally, so the user can resume
   * editing and retry.
   *
   * Pass the token returned by the matching `freezeAutoSave()`: it removes ONLY
   * this owner from the active set, and the freeze lifts only once the LAST
   * owner releases. This is order-independent — an owner that resolves before
   * another still-in-flight owner cannot lift a freeze that owner needs held.
   * A tokenless call releases unconditionally, clearing every owner (explicit
   * navigation cleanup / legacy callers).
   */
  unfreezeAutoSave: (token?: number) => void;
  /** Whether autosave is currently frozen (read at arm time and fire time). */
  isAutoSaveFrozen: () => boolean;
  /**
   * Force every registered component to resync with the latest known server
   * content, even if it is mid-editing-session. Used by STALE_ACTIONS
   * recovery to show the user what changed, in place, for editors that are
   * still mounted.
   */
  resyncFromServer: () => void;
}

const AutoSaveCoordinationContext =
  createContext<AutoSaveCoordinationContextValue | null>(null);

interface AutoSaveCoordinationProviderProps {
  children: ReactNode;
}

/**
 * Provider that coordinates auto-save state across preview components.
 *
 * Preview components register themselves and report their save state.
 * StickyFooter subscribes to aggregate state and can trigger flush before approval.
 *
 * Uses subscriber pattern with useRef to avoid re-renders on state changes.
 *
 * @example
 * ```tsx
 * <AutoSaveCoordinationProvider>
 *   <EmailActionPreview />
 *   <StickyFooter />
 * </AutoSaveCoordinationProvider>
 * ```
 */
export function AutoSaveCoordinationProvider({
  children,
}: AutoSaveCoordinationProviderProps) {
  // Store registrations in ref to avoid re-renders (like useProposalRefresh pattern)
  const registrationsRef = useRef<Map<string, ComponentRegistration>>(
    new Map()
  );
  // Store subscribers in ref
  const subscribersRef = useRef<Set<(state: SubscriberState) => void>>(
    new Set()
  );
  // Monotonically increasing counter for content change tracking
  const contentVersionRef = useRef(0);
  // Frozen flag — true while an approve/reject is in flight or has resolved
  // the proposal. Ref-based so reads/writes never trigger re-renders.
  const frozenRef = useRef(false);
  // Monotonic counter that mints a unique token for every freezeAutoSave()
  // call. Never reused, so tokens can't collide across independent freezers.
  const freezeOwnerRef = useRef(0);
  // The set of tokens whose freeze is still live. The freeze is reference-
  // counted: freezeAutoSave() adds a token, unfreezeAutoSave(token) removes it,
  // and frozenRef stays true until the set is empty. This lets independent
  // freezers (the approval footers AND a card's own "I don't have this
  // information" exit) share one provider-wide freeze without clobbering each
  // other in ANY resolution order — an owner releasing its own token never
  // lifts a freeze another owner still holds.
  const activeFreezeOwnersRef = useRef<Set<number>>(new Set());

  // Compute aggregate state from registrations
  const computeState = useCallback((): SubscriberState => {
    let hasPendingOrSaving = false;
    let hasError = false;

    registrationsRef.current.forEach(registration => {
      if (
        registration.saveState === 'pending' ||
        registration.saveState === 'saving'
      ) {
        hasPendingOrSaving = true;
      }
      if (registration.saveState === 'error') {
        hasError = true;
      }
    });

    return {
      hasPendingOrSaving,
      hasError,
      contentVersion: contentVersionRef.current,
    };
  }, []);

  // Notify all subscribers of state change
  const notifySubscribers = useCallback(() => {
    contentVersionRef.current++;
    const state = computeState();
    subscribersRef.current.forEach(callback => {
      try {
        callback(state);
      } catch (e) {
        logger.error(
          'Subscriber error',
          e instanceof Error ? e : new Error(String(e)),
          { component: 'proposals', module: 'useAutoSaveCoordination' }
        );
      }
    });
  }, [computeState]);

  const register = useCallback(
    (id: string): RegistrationHandle => {
      // Initialize registration
      registrationsRef.current.set(id, {
        saveState: 'idle',
        flushFn: null,
        getContentFn: null,
        resyncFn: null,
      });

      const setSaveState = (state: SaveState) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.saveState = state;
          notifySubscribers();
        }
      };

      const setFlushFn = (fn: () => Promise<void>) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.flushFn = fn;
        }
      };

      const setContentFn = (fn: () => LiveActionContent | null) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.getContentFn = fn;
        }
      };

      const setResyncFn = (fn: () => void) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.resyncFn = fn;
        }
      };

      const unregister = () => {
        registrationsRef.current.delete(id);
        notifySubscribers();
      };

      return {
        setSaveState,
        setFlushFn,
        setContentFn,
        setResyncFn,
        unregister,
      };
    },
    [notifySubscribers]
  );

  const subscribe = useCallback(
    (callback: (state: SubscriberState) => void) => {
      subscribersRef.current.add(callback);
      // Immediately notify subscriber of current state so they don't miss in-progress saves
      callback(computeState());
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
    [computeState]
  );

  const flushAllAndWait = useCallback(
    async (options?: { excludeIds?: string[] }): Promise<FlushResult> => {
      const excludeIds = new Set(options?.excludeIds ?? []);
      const flushFunctions: (() => Promise<void>)[] = [];

      registrationsRef.current.forEach((registration, id) => {
        if (registration.flushFn && !excludeIds.has(id)) {
          flushFunctions.push(registration.flushFn);
        }
      });

      logger.debug('Flushing components', {
        component: 'autosave',
        count: flushFunctions.length,
      });

      if (flushFunctions.length === 0) {
        logger.debug('Flush completed', {
          component: 'autosave',
          succeeded: 0,
          failed: 0,
        });
        return { success: true, errors: [] };
      }

      // Wrap each flush with a 5-second timeout
      const FLUSH_TIMEOUT_MS = 5000;
      const wrappedFlushes = flushFunctions.map(flushFn => {
        return Promise.race([
          flushFn(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('Flush timeout (5s)')),
              FLUSH_TIMEOUT_MS
            )
          ),
        ]);
      });

      const results = await Promise.allSettled(wrappedFlushes);

      const errors: Error[] = [];
      results.forEach(result => {
        if (result.status === 'rejected') {
          const error =
            result.reason instanceof Error
              ? result.reason
              : new Error(String(result.reason));
          errors.push(error);
        }
      });

      const success = errors.length === 0;
      const succeeded = results.length - errors.length;
      const failed = errors.length;
      logger.debug('Flush completed', {
        component: 'autosave',
        succeeded,
        failed,
        errorCount: errors.length,
      });

      return { success, errors };
    },
    []
  );

  const freezeAutoSave = useCallback(() => {
    freezeOwnerRef.current += 1;
    const token = freezeOwnerRef.current;
    activeFreezeOwnersRef.current.add(token);
    frozenRef.current = true;
    return token;
  }, []);

  const unfreezeAutoSave = useCallback((token?: number) => {
    // A tokenless call (explicit navigation cleanup / legacy callers) releases
    // unconditionally, dropping every owner. A tokened call removes only that
    // owner from the active set; the freeze lifts only once the set is empty,
    // so an owner that resolves while another is still in flight never clears a
    // freeze the other owner still needs held — order-independent by construction.
    if (token === undefined) {
      activeFreezeOwnersRef.current.clear();
    } else {
      activeFreezeOwnersRef.current.delete(token);
    }
    if (activeFreezeOwnersRef.current.size === 0) {
      frozenRef.current = false;
    }
  }, []);

  const isAutoSaveFrozen = useCallback(() => frozenRef.current, []);

  const resyncFromServer = useCallback(() => {
    registrationsRef.current.forEach(registration => {
      try {
        registration.resyncFn?.();
      } catch (e) {
        logger.error(
          'Resync error',
          e instanceof Error ? e : new Error(String(e)),
          { component: 'proposals', module: 'useAutoSaveCoordination' }
        );
      }
    });
  }, []);

  const collectAllContent = useCallback((): LiveActionContent[] => {
    const results: LiveActionContent[] = [];

    registrationsRef.current.forEach(registration => {
      if (registration.getContentFn) {
        const content = registration.getContentFn();
        if (content) {
          results.push(content);
        }
      }
    });

    return results;
  }, []);

  const contextValue = useMemo(
    () => ({
      register,
      subscribe,
      flushAllAndWait,
      collectAllContent,
      freezeAutoSave,
      unfreezeAutoSave,
      isAutoSaveFrozen,
      resyncFromServer,
    }),
    [
      register,
      subscribe,
      flushAllAndWait,
      collectAllContent,
      freezeAutoSave,
      unfreezeAutoSave,
      isAutoSaveFrozen,
      resyncFromServer,
    ]
  );

  return React.createElement(
    AutoSaveCoordinationContext,
    { value: contextValue },
    children
  );
}

/**
 * Hook to access auto-save coordination functionality.
 *
 * Must be used within an AutoSaveCoordinationProvider.
 * Throws if no provider is found.
 *
 * @returns Context value with register, subscribe, and flushAllAndWait
 */
export function useAutoSaveCoordination(): AutoSaveCoordinationContextValue {
  const context = useContext(AutoSaveCoordinationContext);
  if (!context) {
    throw new Error(
      'useAutoSaveCoordination must be used within AutoSaveCoordinationProvider'
    );
  }
  return context;
}

/**
 * Hook to optionally access auto-save coordination functionality.
 *
 * Returns null if no provider is found (graceful fallback).
 * Use this in StickyFooter to handle cases where coordination is not available.
 *
 * @returns Context value or null if no provider
 */
export function useAutoSaveCoordinationOptional(): AutoSaveCoordinationContextValue | null {
  return useContext(AutoSaveCoordinationContext);
}
